// src/components/ProductGrid.js - Mobile-friendly version with better button layout
import React, { useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  CardMedia,
  CardActions,
  Slide,
  Skeleton,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  ShoppingCart,
  Search,
  Clear,
  Star,
  MoreVert,
  Link as LinkIcon,
  Share as ShareIcon,
  ContentCopy,
} from "@mui/icons-material";
import ItemDetailsModal from "./ItemDetailsModal";

const ProductGrid = ({
  itemsLoading,
  items,
  filteredItems,
  displayedItemsCount,
  searchQuery,
  selectedCategory,
  handleClearFilters,
  formatPrice,
  getItemCartQuantity,
  cartItems = [], // Add cartItems prop to force re-render when cart changes
  addItemToCart,
  canAddMoreToCart,
  setSelectedItem,
  setShowItemCheckout,
  isLoadingMore,
  loadMoreItems,
  primaryBlue,
  glassStyles,
  cardGlassStyles,
  handleBuyNow,
  modalOpen,
  setModalOpen,
  selectedItemForModal,
  setSelectedItemForModal,
  hideHeader = false, // Add this prop for PublicHomePage
  onRequestItem, // Add this prop for requesting items
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedItemForMenu, setSelectedItemForMenu] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState(false);

  const handleItemClick = (item) => {
    setSelectedItemForModal(item);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleMenuOpen = (e, item) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setSelectedItemForMenu(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedItemForMenu(null);
  };

  const handleShareItem = (item) => {
    const baseUrl = window.location.origin;
    const itemShareLink = `${baseUrl}/item/${item.id}?name=${encodeURIComponent(
      item.name
    )}&price=${item.sellingPrice}`;

    // Always prioritize WhatsApp sharing for better user experience
    const shareMessage = `🛍️ Check out this product: ${
      item.name
    } - ${formatPrice(item.sellingPrice)}\n${itemShareLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      shareMessage
    )}`;
    window.open(whatsappUrl, "_blank");
    
    handleMenuClose();
  };

  const handleCopyLink = async (item) => {
    const baseUrl = window.location.origin;
    const itemShareLink = `${baseUrl}/item/${item.id}?name=${encodeURIComponent(
      item.name
    )}&price=${item.sellingPrice}`;

    try {
      await navigator.clipboard.writeText(itemShareLink);
      console.log("Link copied to clipboard:", itemShareLink);
      setCopySnackbar(true);
    } catch (error) {
      console.error("Failed to copy link:", error);
      // Fallback: show the link in a prompt
      prompt("Copy this link:", itemShareLink);
    }
    handleMenuClose();
  };

  // Smart Add to Cart function - checks if item needs variant selection
  const handleAddToCartClick = (e, item) => {
    e.stopPropagation(); // Prevent card click event

    // Check if item has variants that need to be selected
    const hasVariants =
      (item.sizes && item.sizes.length > 0) ||
      (item.colors && item.colors.length > 0);

    if (hasVariants) {
      // Open modal for variant selection
      setSelectedItemForModal(item);
      setModalOpen(true);
    } else {
      // Add directly to cart with minimum required quantity
      const quantity = item.minOrderQuantity || 1;
      addItemToCart(item, quantity);
    }
  };

  return (
    <>
      {/* Enhanced Filter Bar - Conditionally show based on hideHeader prop */}
      {!hideHeader && (
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              ...glassStyles,
              p: 3,
              bgcolor: "rgba(255, 255, 255, 0.3)",
              backdropFilter: "blur(20px)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: primaryBlue[700],
                }}
              >
                Products
              </Typography>
              {(searchQuery || selectedCategory !== "All") && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<Clear />}
                  sx={{
                    borderColor: primaryBlue[300],
                    color: primaryBlue[600],
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: primaryBlue[500],
                      bgcolor: "rgba(0, 114, 206, 0.1)",
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Enhanced Products Grid */}
      {itemsLoading ? (
        <Grid container spacing={4}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
            <Grid item xs={6} sm={6} md={4} lg={3} key={item}>
              <Card
                elevation={0}
                sx={{
                  ...cardGlassStyles,
                  height: 400,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  height={200}
                  sx={{ borderRadius: "12px 12px 0 0" }}
                />
                <CardContent>
                  <Skeleton variant="text" width="80%" height={30} />
                  <Skeleton variant="text" width="60%" height={25} />
                  <Skeleton variant="text" width="100%" height={60} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (searchQuery && filteredItems.length === 0) || (!searchQuery && !itemsLoading && items.length === 0) ? (
        <Card
          elevation={0}
          sx={{
            ...glassStyles,
            textAlign: "center",
            py: 8,
            bgcolor: "rgba(255, 255, 255, 0.3)",
          }}
        >
          <CardContent>
            {searchQuery ? (
              <Search
                sx={{
                  fontSize: 100,
                  mb: 3,
                  color: primaryBlue[300],
                  opacity: 0.7,
                }}
              />
            ) : (
              <ShoppingCart
                sx={{
                  fontSize: 100,
                  mb: 3,
                  color: primaryBlue[300],
                  opacity: 0.7,
                }}
              />
            )}
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                color: primaryBlue[600],
                fontWeight: "bold",
                mb: 2,
              }}
            >
              {searchQuery ? "No Products Found" : "Amazing Products Coming Soon"}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: primaryBlue[500],
                fontWeight: 500,
                mb: 3,
              }}
            >
              {searchQuery 
                ? "Try adjusting your search or filters to find what you're looking for."
                : "We're curating incredible new products just for you. Check back soon!"
              }
            </Typography>
            {/* Debug info - remove this later */}
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'red' }}>
                DEBUG: items.length={items.length}, filteredItems.length={filteredItems.length}, searchQuery="{searchQuery}", selectedCategory="{selectedCategory}"
              </Typography>
            )}
            {searchQuery && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleClearFilters}
                  sx={{
                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                    fontWeight: "bold",
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Clear All Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => onRequestItem && onRequestItem(searchQuery)}
                  sx={{
                    borderColor: primaryBlue[500],
                    color: primaryBlue[600],
                    fontWeight: "bold",
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: primaryBlue[600],
                      bgcolor: `rgba(${primaryBlue[500]}, 0.1)`,
                    },
                  }}
                >
                  Request for this item
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {filteredItems.slice(0, displayedItemsCount).map((item, index) => {
              // Check if item has variants
              const hasVariants =
                (item.sizes && item.sizes.length > 0) ||
                (item.colors && item.colors.length > 0);
              
              // Check if max quantity is already in cart - use cartItems prop for real-time accuracy
              const cartItem = cartItems.find((ci) => ci.id === item.id);
              const cartQuantity = cartItem ? cartItem.quantity : 0;
              // Only use limits if they are positive numbers
              const stockLimit = (item.stockQuantity && item.stockQuantity > 0) ? item.stockQuantity : Infinity;
              const maxOrderLimit = (item.maxOrderQuantity && item.maxOrderQuantity > 0) ? item.maxOrderQuantity : Infinity;
              const maxAllowedQuantity = Math.min(stockLimit, maxOrderLimit);
              // Max is reached only if there's a limit AND cart already has that many (can't add more)
              const isMaxQuantityReached = maxAllowedQuantity !== Infinity && cartQuantity >= maxAllowedQuantity;

              return (
                <Grid item xs={6} sm={6} md={4} lg={3} key={item.id}>
                  <Slide direction="up" in timeout={300 + index * 50}>
                    <Card
                      elevation={0}
                      sx={{
                        ...cardGlassStyles,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        backdropFilter: "blur(20px)",
                        "&:hover": {
                          transform: "translateY(-12px)",
                          boxShadow: "0 20px 60px rgba(0, 114, 206, 0.25)",
                          bgcolor: "rgba(255, 255, 255, 0.35)",
                        },
                        cursor: "pointer",
                        position: "relative",
                      }}
                      onClick={() => handleItemClick(item)}
                    >
                      {/* Video indicator - positioned absolutely in top left */}
                      {item.videoUrl && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            bgcolor: "rgba(0, 0, 0, 0.8)",
                            color: "white",
                            borderRadius: "12px",
                            px: 1,
                            py: 0.5,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            zIndex: 1,
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          ▶ VIDEO
                        </Box>
                      )}

                      {/* More options button - positioned absolutely in top right */}
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, item)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(255, 255, 255, 0.9)",
                          backdropFilter: "blur(10px)",
                          zIndex: 1,
                          "&:hover": {
                            bgcolor: "rgba(255, 255, 255, 1)",
                          },
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>

                      <CardMedia
                        component="img"
                        height="220"
                        image={item.imageUrl || "/api/placeholder/300/220"}
                        alt={item.name}
                        sx={{
                          objectFit: "cover",
                          borderRadius: "16px 16px 0 0",
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "0.9rem", sm: "1.1rem" },
                            color: primaryBlue[800],
                            mb: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.name}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mb: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            label={item.category}
                            size="small"
                            sx={{
                              ...glassStyles,
                              bgcolor: "rgba(0, 114, 206, 0.8)",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />

                          {/* Show variant indicators */}
                          {hasVariants && (
                            <Chip
                              label="Options Available"
                              size="small"
                              sx={{
                                bgcolor: "rgba(255, 152, 0, 0.8)",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                              }}
                            />
                          )}
                        </Box>

                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "1.2rem", sm: "1.5rem" },
                            color: "text.primary",
                            mb: item.compareAtPrice && item.compareAtPrice > item.sellingPrice ? 0.5 : 1,
                          }}
                        >
                          {formatPrice(item.sellingPrice)}
                        </Typography>

                        {/* MRP Discount Information */}
                        {item.compareAtPrice && item.compareAtPrice > item.sellingPrice && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                textDecoration: "line-through",
                                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                                display: "inline",
                                mr: 1,
                              }}
                            >
                              MRP: {formatPrice(item.compareAtPrice)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#00BFFF",
                                fontWeight: 600,
                                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                                display: "inline",
                              }}
                            >
                              ({Math.round(((item.compareAtPrice - item.sellingPrice) / item.compareAtPrice) * 100)}% OFF)
                            </Typography>
                          </Box>
                        )}

                        <Typography
                          variant="body2"
                          sx={{
                            color: primaryBlue[600],
                            mb: 2,
                            lineHeight: 1.5,
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.description?.substring(0, 80)}...
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: {
                                xs: "0.7rem",
                                sm: "0.875rem",
                              },
                              color:
                                item.stockQuantity > 0
                                  ? "success.main"
                                  : "error.main",
                            }}
                          >
                            {item.stockQuantity > 0
                              ? `✓ Stock (${item.stockQuantity})`
                              : "✗ Out of Stock"}
                          </Typography>
                          {getItemCartQuantity(item.id) > 0 && (
                            <Chip
                              label={`${getItemCartQuantity(item.id)} in cart`}
                              size="small"
                              sx={{
                                ...glassStyles,
                                bgcolor: "rgba(76, 175, 80, 0.8)",
                                color: "white",
                                fontWeight: 600,
                                fontSize: {
                                  xs: "0.6rem",
                                  sm: "0.75rem",
                                },
                              }}
                            />
                          )}
                        </Box>
                      </CardContent>

                      {/* SIMPLIFIED MOBILE-FRIENDLY CARD ACTIONS */}
                      <CardActions sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
                        {/* MOBILE: Show only main action button, desktop: show all */}
                        <Box
                          sx={{
                            display: { xs: "block", md: "none" },
                            width: "100%",
                          }}
                        >
                          {/* Mobile: Single full-width button */}
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={(e) => handleAddToCartClick(e, item)}
                            disabled={item.stockQuantity === 0 || isMaxQuantityReached}
                            sx={{
                              py: 1.2,
                              fontSize: "0.8rem",
                              background: isMaxQuantityReached 
                                ? "rgba(76, 175, 80, 0.8)"
                                : `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                              color: "white",
                              fontWeight: 600,
                              borderRadius: "10px",
                              "&:hover": {
                                background: isMaxQuantityReached
                                  ? "rgba(76, 175, 80, 0.9)"
                                  : `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                                transform: "translateY(-1px)",
                              },
                              "&:disabled": {
                                opacity: isMaxQuantityReached ? 0.8 : 0.5,
                                background: isMaxQuantityReached 
                                  ? "rgba(76, 175, 80, 0.6)" 
                                  : "rgba(0,0,0,0.1)",
                                color: isMaxQuantityReached ? "white" : undefined,
                              },
                              transition:
                                "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                          >
                            {isMaxQuantityReached 
                              ? `Max (${maxAllowedQuantity}) in Cart` 
                              : hasVariants 
                                ? "Select Options" 
                                : "Add to Cart"}
                          </Button>
                        </Box>

                        {/* DESKTOP: Show only main action button */}
                        <Box
                          sx={{
                            display: { xs: "none", md: "flex" },
                            width: "100%",
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => handleAddToCartClick(e, item)}
                            disabled={item.stockQuantity === 0 || isMaxQuantityReached}
                            fullWidth
                            sx={{
                              py: 1,
                              fontSize: "0.875rem",
                              borderColor: isMaxQuantityReached ? "rgba(76, 175, 80, 0.5)" : primaryBlue[300],
                              color: isMaxQuantityReached ? "rgba(76, 175, 80, 0.8)" : primaryBlue[600],
                              fontWeight: 600,
                              bgcolor: "rgba(255, 255, 255, 0.7)",
                              backdropFilter: "blur(10px)",
                              borderRadius: "10px",
                              "&:hover": {
                                borderColor: isMaxQuantityReached ? "rgba(76, 175, 80, 0.7)" : primaryBlue[500],
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                                transform: "translateY(-2px)",
                              },
                              "&:disabled": {
                                opacity: isMaxQuantityReached ? 0.8 : 0.5,
                                borderColor: isMaxQuantityReached ? "rgba(76, 175, 80, 0.5)" : undefined,
                                color: isMaxQuantityReached ? "rgba(76, 175, 80, 0.7)" : undefined,
                              },
                              transition:
                                "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                          >
                            {isMaxQuantityReached 
                              ? `Max (${maxAllowedQuantity}) in Cart` 
                              : hasVariants 
                                ? "Select Options" 
                                : "Add to Cart"}
                          </Button>
                        </Box>
                      </CardActions>
                    </Card>
                  </Slide>
                </Grid>
              );
            })}
          </Grid>

          {/* Share Menu for Mobile (accessed via MoreVert button) */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                ...glassStyles,
                bgcolor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                minWidth: 200,
              },
            }}
          >
            <MenuItem
              onClick={() =>
                selectedItemForMenu && handleCopyLink(selectedItemForMenu)
              }
            >
              <ListItemIcon>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy Link</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                selectedItemForMenu && handleShareItem(selectedItemForMenu)
              }
            >
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Share</ListItemText>
            </MenuItem>
          </Menu>

          {/* Load More Button / Loading State */}
          {displayedItemsCount < filteredItems.length && (
            <Box sx={{ textAlign: "center", mt: 6 }}>
              {isLoadingMore ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ color: primaryBlue[600] }}>
                    Loading more products...
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  onClick={loadMoreItems}
                  sx={{
                    py: 2,
                    px: 4,
                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: "12px",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    boxShadow: "0 8px 32px rgba(0, 114, 206, 0.3)",
                    "&:hover": {
                      background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 40px rgba(0, 114, 206, 0.4)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Load More Products
                </Button>
              )}
            </Box>
          )}
        </>
      )}

      {/* Item Details Modal */}
      <ItemDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        item={selectedItemForModal}
        formatPrice={formatPrice}
        addItemToCart={addItemToCart}
        canAddMoreToCart={canAddMoreToCart}
        setSelectedItem={setSelectedItem}
        setShowItemCheckout={setShowItemCheckout}
        getItemCartQuantity={getItemCartQuantity}
        cartItems={cartItems}
        primaryBlue={primaryBlue}
        glassStyles={glassStyles}
      />

      {/* Copy Link Confirmation Snackbar */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setCopySnackbar(false)} 
          severity="success" 
          variant="filled"
          sx={{ 
            width: '100%',
            backgroundColor: primaryBlue[600],
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          🔗 Link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductGrid;
